import { useEffect, useState } from 'react';
import { Trophy, Medal, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { getToken } from '../../lib/auth';
import { Link } from 'react-router-dom';

interface LoyaltyLeaderboardWidgetProps {
  title?: string;
  /** Number of top entries to show — defaults to 5 */
  limit?: number;
  /** 'monthly' | 'weekly' — defaults to 'monthly' */
  periodType?: string;
  programId?: string;
}

interface LeaderboardEntry {
  customerId: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  score: number;
  rank: number;
  deltaRank: number | null;
}

interface LeaderboardRank {
  rank: number | null;
  score: number;
  totalParticipants: number;
}

interface LeaderboardResponse {
  periodType: string;
  periodKey: string;
  top: LeaderboardEntry[];
  myRank: LeaderboardRank;
}

const MEDAL_COLORS: Record<number, string> = {
  1: 'text-amber-400',
  2: 'text-zinc-400',
  3: 'text-orange-400',
};

function RankBadge({ rank }: { rank: number }) {
  const color = MEDAL_COLORS[rank];
  if (color) {
    return <Medal size={16} className={color} />;
  }
  return <span className="text-xs font-bold text-zinc-400 w-4 text-center">#{rank}</span>;
}

function DeltaIndicator({ delta }: { delta: number | null }) {
  if (delta === null || delta === 0) return null;
  if (delta > 0) {
    return <span className="text-xs text-green-500 font-semibold">+{delta}</span>;
  }
  return <span className="text-xs text-red-400 font-semibold">{delta}</span>;
}

/**
 * CMS block: loyalty_leaderboard
 * Shows a compact top-N leaderboard + the authenticated customer's own position.
 */
export function LoyaltyLeaderboardWidget({ props }: { props: LoyaltyLeaderboardWidgetProps }) {
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const authed = !!getToken();

  const periodType = props.periodType ?? 'monthly';
  const limit = props.limit ?? 5;

  useEffect(() => {
    if (!props.programId) {
      setLoading(false);
      return;
    }

    // Always fetch regardless of auth status — leaderboard is public
    const token = getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    api
      .get<LeaderboardResponse>(
        `/customer/loyalty/leaderboard?programId=${props.programId}&periodType=${periodType}&limit=${limit}`,
        { headers },
      )
      .then((res) => {
        setData(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [props.programId, periodType, limit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 size={20} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!data || data.top.length === 0) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-5 py-8 text-center">
        <Trophy size={28} className="mx-auto mb-3 text-zinc-300" />
        <p className="text-sm text-zinc-500">No rankings yet for this period.</p>
      </div>
    );
  }

  const periodLabel = periodType === 'weekly' ? 'Weekly' : 'Monthly';

  return (
    <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy size={18} className="text-amber-500" />
          <h3 className="text-sm font-semibold text-zinc-800">
            {props.title ?? `${periodLabel} Leaderboard`}
          </h3>
        </div>
        <span className="text-xs text-zinc-400">{data.periodKey}</span>
      </div>

      {/* Top entries */}
      <ul className="divide-y divide-zinc-50">
        {data.top.map((entry) => {
          const name = [entry.firstName, entry.lastName].filter(Boolean).join(' ') || 'Anonymous';
          return (
            <li key={entry.customerId} className="flex items-center gap-3 px-5 py-3">
              <div className="flex-shrink-0 w-5 flex items-center justify-center">
                <RankBadge rank={entry.rank} />
              </div>

              {entry.avatarUrl ? (
                <img
                  src={entry.avatarUrl}
                  alt={name}
                  className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-zinc-500">
                    {(entry.firstName?.[0] ?? entry.lastName?.[0] ?? '?').toUpperCase()}
                  </span>
                </div>
              )}

              <span className="flex-1 text-sm text-zinc-700 truncate">{name}</span>

              <div className="flex items-center gap-1.5 flex-shrink-0">
                <DeltaIndicator delta={entry.deltaRank} />
                <span className="text-sm font-semibold text-zinc-900 tabular-nums">
                  {entry.score.toLocaleString()}
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Customer's own position (only when authenticated and not already in top) */}
      {authed &&
        data.myRank.rank !== null &&
        !data.top.some((e) => e.rank === data.myRank.rank) && (
          <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-100 flex items-center gap-3">
            <span className="text-xs text-zinc-400 flex-shrink-0">You</span>
            <span className="flex-1 text-sm font-semibold text-zinc-700">#{data.myRank.rank}</span>
            <span className="text-sm font-semibold text-zinc-900 tabular-nums">
              {data.myRank.score.toLocaleString()}
            </span>
          </div>
        )}

      {!authed && (
        <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-100 text-center">
          <p className="text-xs text-zinc-500">
            <Link to="/login" className="underline hover:no-underline text-zinc-700 font-medium">
              Log in
            </Link>{' '}
            to see your position
          </p>
        </div>
      )}
    </div>
  );
}
